import { HttpErrorResponse } from '@angular/common/http';
import {
    Component,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren,
} from '@angular/core';
import {
    AuctionCatalogsService,
    GetAllUserPurchaseOffers200Response,
    PurchaseOfferListResponse,
} from '@viewbid/ts-angular-client';
import { VbSnackbarService } from '@viewbid/ui';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { ProductService } from '@/shared/services/product.service';
import { distinctUntilChanged, map, Subject, takeUntil } from 'rxjs';
import { DeviceCheckService } from 'src/app/viewbid/shared/services/device-check.service';
import {
    navigateProfilePaging,
    ProfilePaging,
    resolveProfilePaging,
} from 'src/app/viewbid/shared/utils/profile-paging.util';
import { ActivatedRoute, Params, Router } from '@angular/router';

@Component({
    selector: 'vb-user-purchase-offers',
    templateUrl: './user-purchase-offers.component.html',
    styleUrls: ['./user-purchase-offers.component.scss'],
})
export class UserPurchaseOffersComponent implements OnInit, OnDestroy {
    isLoading: boolean = false;
    pageNo: number = 1;
    pageSize: number = 10;
    private readonly defaultPageSize: number = this.pageSize;
    public readonly pageSizeOptions: ReadonlyArray<number> = [10, 25, 50];
    totalCount: number = 0;
    paginate: any = {};
    unableToLoad: boolean = false;
    userOffers: PurchaseOfferListResponse[] = [];
    currentImageIndex: number | null = null;
    @ViewChildren(PopoverDirective) popovers!: QueryList<PopoverDirective>;
    private mobile$ = new Subject<void>();
    private destroy$ = new Subject<void>();
    public isMobile: boolean = false;

    constructor(
        private auctionCatalogsService: AuctionCatalogsService,
        private snackbarService: VbSnackbarService,
        public productService: ProductService,
        private deviceCheckService: DeviceCheckService,
        private route: ActivatedRoute,
        private router: Router,
    ) {}
    ngOnInit(): void {
        this.route.queryParams
            .pipe(
                map((params: Params): ProfilePaging => {
                    navigateProfilePaging(
                        this.router,
                        this.route,
                        params,
                        this.defaultPageSize,
                        true,
                    );
                    return resolveProfilePaging(params, this.defaultPageSize);
                }),
                distinctUntilChanged(
                    (previous: ProfilePaging, current: ProfilePaging) =>
                        previous.page === current.page &&
                        previous.pageSize === current.pageSize,
                ),
                takeUntil(this.destroy$),
            )
            .subscribe((paging: ProfilePaging) => {
                this.pageNo = paging.page;
                this.pageSize = paging.pageSize;
                this.getUserPurchaseOffers();
            });

        this.deviceCheckService.isMobile$
            .pipe(takeUntil(this.mobile$))
            .subscribe((isMobile: boolean) => {
                this.isMobile = isMobile;
            });
    }

    private async getUserPurchaseOffers(): Promise<void> {
        const paging: ProfilePaging = {
            page: this.pageNo,
            pageSize: this.pageSize,
        };
        this.isLoading = true;
        await this.auctionCatalogsService
            .getAllUserPurchaseOffers(paging.page, paging.pageSize)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: GetAllUserPurchaseOffers200Response) => {
                    if (
                        paging.page !== this.pageNo ||
                        paging.pageSize !== this.pageSize
                    ) {
                        return;
                    }
                    const correctedPaging: ProfilePaging = resolveProfilePaging(
                        paging,
                        this.defaultPageSize,
                        response.totalCount ?? 0,
                    );
                    if (correctedPaging.page !== paging.page) {
                        navigateProfilePaging(
                            this.router,
                            this.route,
                            correctedPaging,
                            this.defaultPageSize,
                            true,
                        );
                        return;
                    }
                    this.isLoading = false;
                    if (response?.data.length) {
                        this.userOffers = response.data;

                        this.totalCount = response.totalCount ?? 0;
                        this.paginate = this.productService.getPager(
                            response.totalCount ?? 0,
                            paging.page,
                            paging.pageSize,
                            false,
                        );
                    } else {
                        this.userOffers = [];
                        this.unableToLoad = true;
                        this.totalCount = 0;
                        this.paginate = this.productService.getPager(
                            0,
                            0,
                            paging.pageSize,
                            false,
                        );
                        this.paginate.currentPage = 0;
                    }
                },
                error: (error: HttpErrorResponse) => {
                    if (
                        paging.page !== this.pageNo ||
                        paging.pageSize !== this.pageSize
                    ) {
                        return;
                    }
                    this.isLoading = false;
                    this.unableToLoad = true;
                    this.snackbarService.error(error.error.message);
                },
            });
    }

    setPage(page: number): void {
        navigateProfilePaging(
            this.router,
            this.route,
            { page, pageSize: this.pageSize },
            this.defaultPageSize,
        );
    }

    setPageSize(pageSize: number): void {
        navigateProfilePaging(
            this.router,
            this.route,
            { page: 1, pageSize },
            this.defaultPageSize,
        );
    }

    limitTitleChars(val: string): string {
        let ellipsisStr: string = val;
        if (val.length >= 25) {
            ellipsisStr = val.substring(0, 30) + ' ...';
        }
        return ellipsisStr;
    }

    showLargeImage(index: number): void {
        this.currentImageIndex = index;
    }

    hideLargeImage(): void {
        this.currentImageIndex = null;
    }

    getOfferStatusClass(status: string): string {
        if (this.isMobile) {
            return 'status-' + status.toLowerCase();
        } else {
            return 'background-' + status.toLowerCase();
        }
    }

    togglePopover(targetPopover: PopoverDirective): void {
        this.popovers.forEach((popover: PopoverDirective) => {
            if (popover === targetPopover) {
                if (popover.isOpen) {
                    popover.show();
                } else {
                    popover.hide();
                }
            } else {
                popover.hide();
            }
        });
    }

    navigateToOfferDetails(offer: any): void {
        this.router.navigate(
            ['/auctions', offer.auctionId, 'lot', offer.lotId],
            {
                queryParams: offer.isAdmin
                    ? { makeAnOffer: true, offerId: offer.id }
                    : {},
            },
        );
    }

    ngOnDestroy(): void {
        this.mobile$.next();
        this.mobile$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
